import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { UndefinedOption } from '@df/ui/atomic/atomic-options';
import { IResizeOptions } from '@df/ui/common/resize.service';
import { DfUiModule } from '@df/ui/df-ui.module';
import { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { YoutubePlayerComponent } from '@df/ui/display/media/youtube-player.component';
import { PhotoSwipeModule } from '@df/ui/photoswipe/photoswipe.module';
import { PhotoswipeOptions } from '@df/ui/photoswipe/photoswipe.types';
import { NICmsContentField } from '@icms/core/content';
import last from 'lodash-es/last';
import { defaultImage } from '../../icms.config';
import { DfImageWrapperComponent } from '@df/module-image/df-image-wrapper.component';
import { DfImageDirective } from '@df/module-image/df-image.directive';

export namespace NImageVideo {
    export interface IData {
        image?: string;
        video?: string;
        videoProvider?: EVideoProviders;
    }
    export enum EVideoProviders {
        vimeo = 'vimeo',
        youtube = 'youtube'
    }
    export const VideoProviderOptions = [
        ...UndefinedOption,
        {
            key: EVideoProviders.youtube,
            title: 'YouTube'
        },
        {
            key: EVideoProviders.vimeo,
            title: 'Vimeo'
        }
    ];
    export const CMS_FIELDS = [
        {
            key: 'image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image',
            default: defaultImage,
            canShow: data => !data?.videoProvider,
            required: true
        },
        <NICmsContentField.IChoiceField>{
            key: 'videoProvider',
            type: NICmsContentField.EFieldType.choice,
            title: 'Video Provider',
            options: NImageVideo.VideoProviderOptions
        },
        {
            key: 'video',
            type: NICmsContentField.EFieldType.text,
            title: 'Video ID or URL',
            canShow: data => !!data?.videoProvider,
            required: true
        }
    ];
}

@Component({
    selector: 'image-video',
    templateUrl: './image-video.html',
    styleUrls: ['./image-video.scss', './has-video-zoom.scss'],
    imports: [DfUiModule, DfImageDirective, PhotoSwipeModule, YoutubePlayerComponent, VimeoPlayerComponent, DfImageWrapperComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageVideoComponent {
    readonly source = input.required<NImageVideo.IData>();
    readonly alt = input<string>();
    readonly photoswipeOption = input<Partial<PhotoswipeOptions>>();
    readonly options = input<IResizeOptions>();
    readonly ratio = input.required<string | number>();
    readonly width = input<number>();

    protected readonly hasImageZoom = computed(() => {
        const source = this.source();
        return !!(this.photoswipeOption() && !source.video && source.image);
    });

    protected readonly hasVideoZoom = computed(() => {
        return !!(this.photoswipeOption() && this.source().video);
    });

    protected readonly photoswipeClass = computed(() => {
        const photoswipeOption = this.photoswipeOption();
        return photoswipeOption ? `photoswipe-index-${photoswipeOption.galleryUID}-${photoswipeOption.index}` : '';
    });

    protected readonly shouldRenderVideo = computed(() => {
        return !!this.source().video && !this.photoswipeOption();
    });

    protected readonly photoswipeImage = computed(() => {
        return this.photoswipeOption() && this.source().image;
    });

    protected readonly photoswipeUid = computed(() => {
        const photoswipeOption = this.photoswipeOption();
        return photoswipeOption && (this.source().image || this.source().video) + '.' + photoswipeOption.galleryUID + '.' + photoswipeOption.index;
    });

    protected readonly isVimeo = computed(() => {
        return this.source().video?.includes('vimeo') || false;
    });

    protected readonly youtubeIframeSource = computed(() => {
        return `https://www.youtube.com/embed/${this.source().video}?autoplay=1&loop=1&modestbranding=1&showinfo=0&controls=0`;
    });

    protected readonly vimeoIframeSource = computed(() => {
        const id = last(this.source().video?.split('/'));
        return `https://player.vimeo.com/video/${id}?background=true&loop=true&controls=false&responsive=true`;
    });

    protected readonly videoIframeHtml = computed(() => {
        const iframeSource = this.iframeSource();

        return iframeSource && this.photoswipeOption()
            ? `<div  style="bottom:7em; left:0; right:0; top:5em; position:absolute;" class="flex flex-justify-center"><div class="ratio-16-9 w-12 bg-col-4" >
            <i class="icon-loading top-50 left-50 z-0 pe-none cursor-pointer"></i>
        <iframe class="fill z-1" src="${iframeSource}"></iframe></div></div>`
            : undefined;
    });

    private readonly iframeSource = computed(() => {
        const source = this.source();
        if (!source.video) {
            return undefined;
        }
        if (this.isVimeo()) {
            return this.vimeoIframeSource();
        }
        return this.youtubeIframeSource();
    });
}
